import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import clsx from "clsx";
import Highlighter from "react-highlight-words";

import TreeComponent from "@components/TreeComponent";
import Loader from "@components/LoaderComponent/loader";
import TitleComponent from "@components/TitleComponent";
import PopupComponent from "@components/PopupComponent";
import ButtonComponent from "@components/ButtonComponent";
import Confirmationpopup from "@components/ConfirmationPopup";
import HierarchyComponent from "@components/HierarchyComponent";
import CustomFileUploader from "@components/CustomFileUploader";
import GradientText from "@/components/GradiantTitle";

import {
  getTenantHierarchy,
  SaveTenantHierarchyFile,
  DownloadTenantHierarchy,
  addToDownloadCenter,
  extractFileName,
  extractFileSize,
  updateDownloadItem,
  GetSFMDisplay,
} from "@helpers/helpers";
import { downloadFile } from "@/helpers/downloadHelper";
import { transformTenantData } from "./helpers";

import SfmModalStore from "@stores/SfmModalStore";
import DownloadModalStore from "@stores/DownloadModalStore";

import { ZERO_VALUE, ONE_VALUE } from "@utils/constants";
import { MODAL_STORE_OPEN_TIMEOUT, MODAL_STORE_HIGHLIGHT_TIMEOUT, FIRST_INDEX, TRANSITION_TIMEOUT } from "./constants";

const TenantDetails = ({ resetComponent }) => {
  const { t: translate, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [tenantOrgs, setTenantOrgs] = useState([]);
  const [openFileDropModal, setOpenFileDropModal] = useState(false);
  const [refreshFileDrop, setRefreshFileDrop] = useState(false);
  const [openConfirmationModal, setOpenConfirmation] = useState(null);
  const [transformData, setTransformedData] = useState({});
  const [treeTransformData, setTreeTransformedData] = useState({});
  const [file, setFile] = useState({});
  const [showTree, setShowTree] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [infoPopupOpen, setInfoPopupOpen] = useState(false);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fileTypes = ["xlsx", "xls"];

  const handleFileDrop = (droppedFile) => {
    setIsLoading(true);
    SaveTenantHierarchyFile(droppedFile, (data) => {
      const sfmData = GetSFMDisplay(data.SFM);
      SfmModalStore.open(sfmData, () => {
        window.location.href = "/tenant-hierarchy";
      });
    });
  };

  const handleChange = (file) => {
    setFile(file);
    setOpenConfirmation({
      actionTypeTag: "FILE_DROP",
      name: translate("tenant-details.actions.FILE_DROP"),
      actionFN: () => handleFileDrop(file),
      cancelAction: () => setRefreshFileDrop(!refreshFileDrop),
      confirmationMessage: {
        text: translate("tenant-details.file-upload"),
        fileName: file.name,
      },
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
  };

  useEffect(() => {
    setIsLoading(true);
    // Use the extracted ID as needed, for example:
    getTenantHierarchy((tenantDetails) => {
      const tenantOrgsDetails = tenantDetails.TenantOrgs;
      setTenantOrgs(tenantOrgsDetails);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (tenantOrgs.length === ZERO_VALUE) return;

    const { transformedData, treeTransformedData } = transformTenantData(
      tenantOrgs,
      i18n.language
    );

    setTransformedData(transformedData);
    setTreeTransformedData(treeTransformedData);
  }, [tenantOrgs]);

  const handleToggle = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setShowTree(!showTree);
      setIsTransitioning(false);
    }, TRANSITION_TIMEOUT); // Match the duration of the CSS transition
  };

  const renderHierarchy = () => {
    let matchingRecords = tenantOrgs
      ?.filter((record) => {
        return record.Level === ZERO_VALUE;
      })
      .map((record, index) => {
        return {
          ...record,
          customID: index,
        };
      });
    let childrenRecords = tenantOrgs.filter((record) => {
      return record.Level !== ZERO_VALUE;
    });

    return (
      <>
        <div className="p-5 w-full flex flex-col gap-5">
          <div className="flex dark:text-white justify-between items-center w-full m-auto">
            <span>
              {translate("tenant-details.columns.TenantLastUpdateDate")}{" "}
              {formatDate(matchingRecords[FIRST_INDEX]?.LastUpdateDate)}
            </span>
            <div className="button-wrapper flex gap-2 items-center">
              <ButtonComponent
                className="text-sm min-w-max h-fit  p-2 rounded-lg px-4 cursor-pointer"
                id="download-details"
                color="blue"
                onClick={() => {
                  const toastId = toast.loading(
                    translate("tenant-details.processing-download")
                  );
                  const newPendingID = addToDownloadCenter({
                    href: "",
                    activityTag: "DownloadTenantHierarchy",
                    done: false,
                  });
                  DownloadTenantHierarchy((data) => {
                    if (data.File) {
                      const fileName = extractFileName(data.File);
                      const fileSize = extractFileSize(data.File);
                      updateDownloadItem({
                        id: newPendingID,
                        newProps: {
                          href: data.File,
                          title: fileName,
                          fileSize: fileSize,
                          done: true,
                        },
                      });
                      toast.update(toastId, {
                        render: translate("tenant-details.download-ready"),
                        type: "success",
                        isLoading: false,
                        autoClose: 3000,
                      });
                      DownloadModalStore.open(MODAL_STORE_OPEN_TIMEOUT);
                      DownloadModalStore.highlight(
                        MODAL_STORE_HIGHLIGHT_TIMEOUT,
                        newPendingID
                      );
                      downloadFile(data.File, "_blank");
                    } else {
                      const sfmData = GetSFMDisplay(data.SFM);
                      toast.update(toastId, {
                        render: `${sfmData.Message}`,
                        type: "warning",
                        isLoading: false,
                        autoClose: 5000,
                      });
                    }
                  });
                }}
                ref={null}
                icon={null}
                text={translate("tenant-details.download")}
              />
              <ButtonComponent
                className="text-sm min-w-max h-fit  p-2 rounded-lg px-4 cursor-pointer"
                id="upload-details"
                color="green"
                onClick={() => setOpenFileDropModal(true)}
                ref={null}
                icon={null}
                text={translate("tenant-details.upload")}
              />
            </div>
          </div>

          <div className="text-lg font-semibold flex justify-between items-center">
            <GradientText >
              {translate("tenant-details.title")}
            </GradientText>
            <button className="flex items-center py-3 dark:text-white" onClick={handleToggle}>
              <span>{translate("tenant-details.diagram")}</span>{" "}
              <input
                type="checkbox"
                className="hidden"
                id="switch"
                {...(showTree && {
                  checked: true,
                })}
                tabIndex={-1}
              />
              <label
                htmlFor="switch"
                className={clsx(
                  `flex items-center ltr:ml-4 rtl:mr-4 cursor-pointer w-10 h-5 rounded-full p-1 transition duration-300 ease-in-out pointer-events-none`,
                  showTree ? "bg-[#c6cdff]" : "bg-gray-400"
                )}
                tabIndex={-1}
              >
                <span
                  className={clsx(
                    `block w-4 h-4 rounded-full bg-white shadow-md transform duration-300 ease-in-out`,
                    showTree ? "ltr:translate-x-4 rtl:-translate-x-4" : ""
                  )}
                />
              </label>
            </button>
          </div>
          <div className="w-full">
            <div>
              {showTree ? (
                <div className="flex justify-center mt-5 w-full">
                  {!!Object.keys(treeTransformData).length && (
                    <TreeComponent transformData={treeTransformData} />
                  )}
                </div>
              ) : (
                <HierarchyComponent
                  data={tenantOrgs}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  setInfoPopupOpen={setInfoPopupOpen}
                  setCurrentBranch={setCurrentBranch}
                  refetchData={resetComponent}
                />
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  return isLoading ? (
    <Loader dots withContainer />
  ) : (
    <>
      <div className="w-full">
        <div className="border-2 dark:border-gray-500 border-gray-200 rounded mr-5 text-black">
          {renderHierarchy()}
        </div>
        {openFileDropModal && (
          <PopupComponent
            externalClassName={"w-[40vw] !max-w-[550px]"}
            open={openFileDropModal}
            rawValue={
              <>
                <div className="py-5">
                  <TitleComponent
                    text={translate("tenant-details.actions.drop-message")}
                    size="lg"
                    description={translate(
                      "tenant-details.actions.drop-description"
                    )}
                  />
                </div>
                <CustomFileUploader
                  key={`fileDrop_${refreshFileDrop}`}
                  classes="h-[100px]"
                  handleChange={handleChange}
                  name="file"
                  types={fileTypes}
                />
              </>
            }
            setPopupOpen={setOpenFileDropModal}
            addBlur
            hideCellValue={true}
          />
        )}
        {openConfirmationModal && (
          <Confirmationpopup
            singleAction={openConfirmationModal}
            setOpenConfirmationAction={setOpenConfirmation}
          />
        )}
      </div>
      {infoPopupOpen === currentBranch?.id && (
        <PopupComponent
          externalClassName={"w-[40vw] !max-w-[550px]"}
          open={infoPopupOpen}
          rawValue={
            <div data-testid="tenant-details-popup">
              <p className="font-semibold text-gray-700 dark:text-white">
                {translate("tenant-details.contact-info")}
              </p>
              {(!!currentBranch.addresses.length ||
                !!currentBranch.contacts.length) && (
                <div className="flex flex-col dark:text-white dark:opacity-75 gap-2 italic opacity-60 items-start justify-between px-5 pl-0 mt-4">
                  <div className="flex flex-col gap-2 items-start">
                    <span className="font-semibold">
                      {translate("tenant-details.address")}
                      {":"}
                    </span>
                    {currentBranch.addresses.map((address, index) => (
                      <span data-testid="popup-address" key={index}>
                        <Highlighter
                          highlightClassName="text-red-500 bg-transparent"
                          searchWords={[searchTerm]}
                          autoEscape={true}
                          textToHighlight={address}
                        />
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-col gap-2">
                    {currentBranch.contacts
                      .slice() // Create a shallow copy to avoid mutating the original array
                      .sort((a, b) =>
                        a.ContactTypeTag.localeCompare(b.ContactTypeTag)
                      ) // Sort by ContactTypeTag
                      .reduce(
                        (acc, contact, index, array) => {
                          const { ContactTypeTag } = contact;
                          if (
                            !acc.lastType ||
                            acc.lastType !== ContactTypeTag
                          ) {
                            acc.lastType = ContactTypeTag;
                            acc.currentIndex = 0;
                          } else {
                            acc.currentIndex += 1;
                          }
                          acc.contacts.push({
                            ...contact,
                            displayIndex: acc.currentIndex,
                          });
                          return acc;
                        },
                        { contacts: [], lastType: null, currentIndex: 0 }
                      )
                      .contacts.map((contact, index) => {
                        const contactType =
                          contact.ContactTypeTag === "MASTERMOBILE"
                            ? translate("tenant-details.phone")
                            : contact.ContactTypeTag === "MASTEREMAIL"
                            ? translate("tenant-details.email")
                            : translate("tenant-details.contact");
                        return (
                          <span key={index} className="flex flex-col mt-2">
                            {" "}
                            <span
                              data-testid={`popup-contact-${contactType}`}
                              className="font-semibold"
                            >
                              {`${contactType} ${
                                contact.displayIndex + ONE_VALUE
                              }:`}
                            </span>
                            <Highlighter
                              highlightClassName="text-red-500 bg-transparent"
                              searchWords={[searchTerm]}
                              autoEscape={true}
                              textToHighlight={contact.ContactValue}
                            />
                          </span>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          }
          setPopupOpen={setInfoPopupOpen}
          addBlur
          hideCellValue={true}
        />
      )}
    </>
  );
};
export default TenantDetails;
